// TL header.
class TLHeader extends HTMLElement {
  constructor() {
    super();
    this._onSectionLoad = this._onSectionLoad.bind(this);
  }

  connectedCallback() {
    if (this._abortCtrl) this.disconnectedCallback();

    this._abortCtrl = new AbortController();
    this._eventOptions = { signal: this._abortCtrl.signal };
    document.addEventListener('shopify:section:load', this._onSectionLoad, this._eventOptions);

    this.sticky = this.dataset.sticky === 'true';
    this.headroom = this.dataset.headroom === 'true' && this.sticky;
    this.headroomThresholdSetting = parseInt(this.dataset.headroomThreshold || '0', 10);
    this.lastY = window.scrollY;
    this._scrollPending = false;

    if (this.sticky) {
      this._onScrollBound = this._onScroll.bind(this);
      window.addEventListener('scroll', this._onScrollBound, { passive: true, signal: this._abortCtrl.signal });
      this._update();
    }

    this._measureHeightNow();
    this._resizeObs = new ResizeObserver(() => this._publishHeight());
    this._resizeObs.observe(this);
    this._onWinResizeBound = () => this._publishHeight();
    window.addEventListener('resize', this._onWinResizeBound, { passive: true, signal: this._abortCtrl.signal });

    this.initAnnouncement();
    this.initMobileToggle();
    this.initMegaMenu();
    this.initCartCount();
    this.initCartDrawer();
    this.initVoiceSearch();

    requestAnimationFrame(() => {
      this.classList.add('is-ready');
    });
  }

  disconnectedCallback() {
    this._abortCtrl?.abort();
    this._abortCtrl = null;
    this._resizeObs?.disconnect();
    this._resizeObs = null;
    cancelAnimationFrame(this._heightRaf);
    this._heightRaf = null;
    this._recog?.abort?.();
    this._recog = null;
    this.__predictiveSearch = null;
    this.__cartDrawer = null;
  }

  // cached element refs
  get _predictiveSearchEl() {
    if (!this.__predictiveSearch) this.__predictiveSearch = this.querySelector('tl-predictive-search');
    return this.__predictiveSearch;
  }

  get _cartDrawerEl() {
    if (!this.__cartDrawer) this.__cartDrawer = document.querySelector('cart-drawer-component');
    return this.__cartDrawer;
  }

  _onSectionLoad(event) {
    if (!event.target?.contains?.(this)) return;
    this.disconnectedCallback();
    this.connectedCallback();
  }

  _measureHeightNow() {
    const h = Math.round(this.getBoundingClientRect().height);
    if (h === this._naturalHeight) return;
    this._naturalHeight = h;
    document.documentElement.style.setProperty('--header-sticky-height', h + 'px');
    document.documentElement.style.setProperty('--header-group-height', h + 'px');
    document.documentElement.style.setProperty('--header-height', h + 'px');
  }

  _publishHeight() {
    if (this._heightRaf) return;
    this._heightRaf = requestAnimationFrame(() => {
      this._heightRaf = null;
      this._measureHeightNow();
    });
  }

  _onScroll() {
    if (this._scrollPending) return;
    this._scrollPending = true;
    requestAnimationFrame(() => {
      this._update();
      this._scrollPending = false;
    });
  }

  _update() {
    const y = Math.max(0, window.scrollY);
    this.classList.toggle('is-scrolled', y > 4);

    if (this.headroom) {
      const delta = y - this.lastY;
      const searchOpen = !!this._predictiveSearchEl?.classList.contains('is-open');
      const dialogOpen = !!this._cartDrawerEl?.querySelector('dialog[open], dialog.dialog-modal[open]');
      const threshold = Math.max(this.headroomThresholdSetting, this._naturalHeight || 0);
      if (y <= threshold || searchOpen || dialogOpen) {
        this.classList.remove('is-sticky-hidden');
      } else if (delta > 2) {
        this.classList.add('is-sticky-hidden');
      } else if (delta < -2) {
        this.classList.remove('is-sticky-hidden');
      }
    }

    this.lastY = y;
  }

  initAnnouncement() {
    const bar = this.querySelector('[data-announcement]');
    const close = this.querySelector('[data-announcement-close]');
    if (!bar || !close) return;
    // Session-only UI state.
    const key = 'tl_announcement_dismissed';
    if (sessionStorage.getItem(key) === '1') bar.setAttribute('data-dismissed', 'true');
    close.addEventListener('click', () => {
      bar.setAttribute('data-dismissed', 'true');
      sessionStorage.setItem(key, '1');
    }, this._eventOptions);
  }

  initMobileToggle() {
    const toggle = this.querySelector('[data-mobile-toggle]');
    if (!toggle) return;
    toggle.addEventListener('click', () => {
      const drawer = document.getElementById('tl-mobile-drawer');
      if (!drawer) return;
      document.dispatchEvent(new CustomEvent('tl:drawer:open', { detail: { trigger: toggle } }));
    }, this._eventOptions);
  }

  initMegaMenu() {
    const items = Array.from(this.querySelectorAll('.menu__item.has-mega'));
    if (!items.length) return;

    const openItem = (item, focusFirst = false) => {
      const link = item.querySelector('.menu__link[aria-haspopup]');
      item.dataset.megaOpen = 'true';
      link?.setAttribute('aria-expanded', 'true');
      if (focusFirst) {
        const first = item.querySelector('.mega a, .mega button');
        first?.focus({ preventScroll: true });
      }
    };

    const closeItem = (item) => {
      const link = item.querySelector('.menu__link[aria-haspopup]');
      delete item.dataset.megaOpen;
      link?.setAttribute('aria-expanded', 'false');
    };

    const closeAll = (except = null) => {
      items.forEach((item) => {
        if (item !== except) closeItem(item);
      });
    };

    items.forEach((item, index) => {
      const link = item.querySelector('.menu__link[aria-haspopup]');
      const mega = item.querySelector('[data-mega]');
      if (!link || !mega) return;

      // hover-open is for fine pointers; touch opens via click below
      item.addEventListener('pointerenter', (event) => {
        if (event.pointerType === 'touch') return;
        closeAll(item);
        openItem(item);
      }, this._eventOptions);

      item.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'touch') return;
        closeItem(item);
      }, this._eventOptions);

      item.addEventListener('focusin', () => {
        // touch focus would pre-empt the tap-to-open click guard
        if (window.matchMedia('(hover: none)').matches) return;
        closeAll(item);
        openItem(item);
      }, this._eventOptions);

      item.addEventListener('focusout', (event) => {
        const next = event.relatedTarget;
        if (!(next instanceof Node) || !item.contains(next)) {
          closeItem(item);
        }
      }, this._eventOptions);

      link.addEventListener('keydown', (event) => {
        const isOpen = item.dataset.megaOpen === 'true';
        switch (event.key) {
          case 'Enter':
            if (!isOpen) {
              event.preventDefault();
              closeAll(item);
              openItem(item, true);
            }
            break;
          case ' ':
          case 'ArrowDown':
            event.preventDefault();
            closeAll(item);
            openItem(item, true);
            break;
          case 'Escape':
            event.preventDefault();
            closeItem(item);
            link.focus();
            break;
          case 'ArrowRight': {
            event.preventDefault();
            const next = items[index + 1] || items[0];
            closeAll();
            next.querySelector('.menu__link')?.focus();
            break;
          }
          case 'ArrowLeft': {
            event.preventDefault();
            const previous = items[index - 1] || items[items.length - 1];
            closeAll();
            previous.querySelector('.menu__link')?.focus();
            break;
          }
          default:
            break;
        }
      }, this._eventOptions);

      link.addEventListener('click', (event) => {
        if (!window.matchMedia('(hover: none)').matches) return;
        if (item.dataset.megaOpen === 'true') return;
        event.preventDefault();
        closeAll(item);
        openItem(item);
      }, this._eventOptions);

      mega.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        closeItem(item);
        link.focus();
      }, this._eventOptions);
    });
  }

  initCartCount() {
    const el = this.querySelector('[data-cart-count]');
    if (!el) return;
    const apply = (count) => {
      if (typeof count !== 'number' || !Number.isFinite(count)) return;
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    };
    // Cart event compatibility.
    document.addEventListener('cart:updated', (e) => apply(e.detail?.cart?.item_count), this._eventOptions);
    document.addEventListener('cart-update', (e) => apply(e.detail?.data?.item_count ?? e.detail?.cart?.item_count), this._eventOptions);
    document.addEventListener('cart:update', (e) => {
      const total = e.detail?.resource?.item_count;
      if (typeof total === 'number') return apply(total);
      // read total from DOM
      queueMicrotask(() => {
        const counted = document.querySelector('cart-items-component [ref="cartItemCount"]')?.textContent?.trim();
        if (counted) return apply(parseInt(counted, 10));
        apply(e.detail?.data?.itemCount);
      });
    }, this._eventOptions);
  }

  initCartDrawer() {
    const triggers = this.querySelectorAll('[data-cart-drawer-trigger]');
    if (!triggers.length) return;
    const openDrawer = async (event) => {
      const drawer = document.querySelector('cart-drawer-component');
      if (!drawer) return;
      event.preventDefault();
      await customElements.whenDefined('cart-drawer-component');
      if (drawer.constructor === HTMLElement) customElements.upgrade(drawer);
      if (typeof drawer.open === 'function') drawer.open();
    };
    triggers.forEach((trigger) => trigger.addEventListener('click', openDrawer, this._eventOptions));
  }

  initVoiceSearch() {
    const btn = this.querySelector('[data-voice-search]');
    const input = this.querySelector('.search__input');
    if (!btn || !input) return;
    const Recog = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recog) { btn.style.display = 'none'; return; }
    this._recog?.abort?.();
    const recog = new Recog();
    this._recog = recog;
    this._recogListening = false;
    recog.lang = 'en-GB';
    recog.onresult = (e) => {
      input.value = e.results[0][0].transcript;
      const form = input.form;
      if (form?.requestSubmit) form.requestSubmit();
      else form?.submit();
    };
    recog.onend = () => { this._recogListening = false; };
    recog.onerror = () => { this._recogListening = false; };
    btn.addEventListener('click', () => {
      if (this._recogListening) return;
      try {
        recog.start();
        this._recogListening = true;
      } catch {
        this._recogListening = false;
      }
    }, this._eventOptions);
  }
}

if (!customElements.get('tl-header')) {
  customElements.define('tl-header', TLHeader);
}

// TL OVERRIDE: keyboard-modality class, gates focus rings
const tlModalityKeys = new Set(['Tab', 'Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End']);
document.addEventListener('keydown', (e) => {
  if (tlModalityKeys.has(e.key)) document.documentElement.classList.add('tl-kbd');
}, { capture: true });
document.addEventListener('pointerdown', () => document.documentElement.classList.remove('tl-kbd'), { capture: true, passive: true });

// TL OVERRIDE: prefs dialog focuses its heading, drawer doctrine
document.addEventListener('focusin', (e) => {
  if (e.target?.id !== 'shopify-pc__prefs__header-close') return;
  const dialog = document.getElementById('shopify-pc__prefs__dialog');
  const from = e.relatedTarget;
  if (!dialog || (from && dialog.contains(from))) return;
  const title = document.getElementById('shopify-pc__prefs__header-title');
  if (!title) return;
  title.setAttribute('tabindex', '-1');
  title.focus({ preventScroll: true });
});

